import { useEffect, useRef, useState } from 'react'
import { tokens } from '@/theme'

/**
 * The ambient three.js scenes that sit beside each page's headline.
 *
 *   mark   — the brand mark itself, extruded and drifting. Home and Chat.
 *   rings  — polymer seal geometry, drifting O-rings. Initiatives.
 *
 * **three.js is loaded lazily.** The `import('three')` below is a dynamic
 * import, so Vite emits it as its own chunk the browser fetches on first
 * use; the main bundle pays nothing for it. Rendering degrades in three
 * steps:
 *
 *   1. before the chunk lands, and if WebGL is unavailable (older VDI
 *      images, GPU blocklists, jsdom under test) → the flat SVG mark,
 *      which is the same artwork the header uses;
 *   2. `prefers-reduced-motion` → WebGL renders exactly one frame, no rAF
 *      loop and no pointer parallax;
 *   3. otherwise → the full ambient animation.
 *
 * The canvas is `aria-hidden`; it is decoration sitting beside an h1, and
 * the SVG fallback carries the same (decorative) weight. Nothing here is
 * the only route to any information on the page.
 */

type Variant = 'mark' | 'rings'

interface Props {
  variant?: Variant
  /** Rendered height in px. Width fills the host column. */
  height?: number
}

// three.js wants numeric colors; these are `tokens.gold`, `tokens.inkPanel`
// and the brand cyan used as a cool rim light.
const GOLD = 0x998242
const INK = 0x1a1814
const TEAL = 0x6dc1c9

export function KeystoneScene({ variant = 'mark', height = 500 }: Props) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [webglReady, setWebglReady] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    let cancelled = false
    let teardown: (() => void) | null = null

    void (async () => {
      let THREE: typeof import('three')
      try {
        THREE = await import('three')
      } catch {
        // Chunk failed to load (offline, blocked) — the SVG stays.
        return
      }
      if (cancelled) return

      const canvas = document.createElement('canvas')
      Object.assign(canvas.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        display: 'block',
      })
      canvas.setAttribute('aria-hidden', 'true')

      let renderer: import('three').WebGLRenderer
      try {
        renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
      } catch {
        // No WebGL context available — keep the SVG fallback.
        return
      }
      if (cancelled) {
        renderer.dispose()
        return
      }

      host.appendChild(canvas)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))

      const scene = new THREE.Scene()
      const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100)
      camera.position.set(0, 0, 11)

      scene.add(new THREE.AmbientLight(0xffffff, 1.1))
      const key = new THREE.DirectionalLight(0xfff3d6, 2.2)
      key.position.set(4, 6, 8)
      scene.add(key)
      const rim = new THREE.DirectionalLight(TEAL, 0.6)
      rim.position.set(-6, -2, -4)
      scene.add(rim)

      // pivot = pointer parallax, group = the scene's own drift.
      const pivot = new THREE.Group()
      scene.add(pivot)
      const group = new THREE.Group()
      pivot.add(group)

      /** Per-frame animators, each closing over the object it drives. */
      const spinners: ((t: number) => void)[] = []

      const mat = (color: number, opts: Record<string, number> = {}) =>
        new THREE.MeshStandardMaterial({ color, metalness: 0.55, roughness: 0.35, ...opts })

      if (variant === 'mark') {
        // Geometry derived from the logo SVG (stroke-13 triangle + slash),
        // converted to y-up and centred. Slash parallel to the right edge.
        const outer = new THREE.Shape()
        outer.moveTo(-0.781, 2.533)
        outer.lineTo(2.145, -2.533)
        outer.lineTo(-3.706, -2.533)
        outer.closePath()
        const hole = new THREE.Path()
        hole.moveTo(-0.781, 1.181)
        hole.lineTo(0.975, -1.857)
        hole.lineTo(-2.535, -1.857)
        hole.closePath()
        outer.holes.push(hole)

        const slash = new THREE.Shape()
        slash.moveTo(0.0, 2.533)
        slash.lineTo(0.781, 2.533)
        slash.lineTo(3.706, -2.533)
        slash.lineTo(2.926, -2.533)
        slash.closePath()

        const extrude = {
          depth: 0.55,
          bevelEnabled: true,
          bevelThickness: 0.05,
          bevelSize: 0.05,
          bevelSegments: 3,
        }
        const gold = mat(GOLD, { metalness: 0.75, roughness: 0.28 })
        const tri = new THREE.Mesh(new THREE.ExtrudeGeometry(outer, extrude), gold)
        const bar = new THREE.Mesh(new THREE.ExtrudeGeometry(slash, extrude), gold)
        tri.position.z = -0.275
        bar.position.z = -0.275
        group.add(tri, bar)

        spinners.push(t => {
          group.rotation.y = t * 0.45 + Math.sin(t * 0.3) * 0.5
          group.rotation.x = 0.14 + Math.sin(t * 0.7) * 0.22
          group.rotation.z = Math.sin(t * 0.52) * 0.1
          group.position.y = Math.sin(t * 0.9) * 0.18
        })
      }

      if (variant === 'rings') {
        // Polymer seal geometry — drifting O-rings.
        const specs = [
          { r: 2.1, tube: 0.26, color: GOLD, x: 0.2, y: 0.1, rx: 1.1, ry: 0.3, sy: 0.11, sx: 0.07 },
          {
            r: 1.35,
            tube: 0.2,
            color: INK,
            x: -1.6,
            y: 1.3,
            rx: 0.4,
            ry: 1.2,
            sy: -0.09,
            sx: 0.13,
          },
          {
            r: 0.95,
            tube: 0.16,
            color: TEAL,
            x: 1.9,
            y: -1.5,
            rx: 0.9,
            ry: 0.7,
            sy: 0.15,
            sx: -0.1,
          },
          {
            r: 0.62,
            tube: 0.12,
            color: GOLD,
            x: -2.2,
            y: -1.1,
            rx: 1.4,
            ry: 0.2,
            sy: 0.12,
            sx: 0.18,
          },
          { r: 1.7, tube: 0.1, color: INK, x: 1.1, y: 1.8, rx: 0.2, ry: 0.9, sy: -0.12, sx: 0.05 },
        ]
        specs.forEach((s, i) => {
          const m = new THREE.Mesh(
            new THREE.TorusGeometry(s.r, s.tube, 32, 96),
            mat(s.color, s.color === TEAL ? { metalness: 0.3, roughness: 0.5 } : {}),
          )
          // Depth is staggered deterministically rather than randomly, so the
          // composition is the same every render.
          m.position.set(s.x, s.y, (i / (specs.length - 1) - 0.5) * 1.5)
          m.rotation.set(s.rx, s.ry, 0)
          group.add(m)
          spinners.push(t => {
            m.rotation.x = s.rx + t * s.sx
            m.rotation.y = s.ry + t * s.sy
            m.position.y = s.y + Math.sin(t * 0.6 + s.r * 5) * 0.1
          })
        })
        spinners.push(t => {
          group.rotation.y = Math.sin(t * 0.12) * 0.18
        })
      }

      const resize = () => {
        const w = host.clientWidth || 1
        const h = host.clientHeight || 1
        renderer.setSize(w, h, false)
        camera.aspect = w / h
        camera.updateProjectionMatrix()
      }
      resize()

      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
      ro?.observe(host)

      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      // Pointer parallax — eased toward the cursor, skipped when motion is reduced.
      let px = 0
      let py = 0
      let tx = 0
      let ty = 0
      const onMove = (e: PointerEvent) => {
        tx = (e.clientX / window.innerWidth - 0.5) * 2
        ty = (e.clientY / window.innerHeight - 0.5) * 2
      }
      if (!reduced) window.addEventListener('pointermove', onMove, { passive: true })

      let raf = 0
      const t0 = performance.now()
      const tick = () => {
        const t = (performance.now() - t0) / 1000
        px += (tx - px) * 0.04
        py += (ty - py) * 0.04
        spinners.forEach(f => f(t))
        pivot.rotation.x = py * 0.14
        pivot.rotation.y = px * 0.1
        renderer.render(scene, camera)
        if (!reduced) raf = requestAnimationFrame(tick)
      }
      tick()

      setWebglReady(true)

      teardown = () => {
        cancelAnimationFrame(raf)
        ro?.disconnect()
        window.removeEventListener('pointermove', onMove)
        // Geometries and materials are GPU allocations; React unmounting the
        // host doesn't free them.
        scene.traverse(obj => {
          const withGeo = obj as { geometry?: { dispose(): void }; material?: unknown }
          withGeo.geometry?.dispose()
          const material = withGeo.material
          const materials = Array.isArray(material) ? material : material ? [material] : []
          for (const m of materials) (m as { dispose(): void }).dispose()
        })
        renderer.dispose()
        canvas.remove()
      }
    })()

    return () => {
      cancelled = true
      teardown?.()
    }
  }, [variant])

  return (
    <div
      ref={hostRef}
      style={{
        position: 'relative',
        height,
        alignSelf: 'stretch',
        display: 'block',
      }}
    >
      {/* Flat mark — the pre-WebGL and no-WebGL face of every variant. */}
      <svg
        viewBox="-20 -4 148 102"
        aria-hidden="true"
        focusable="false"
        style={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          width: '46%',
          maxWidth: 260,
          height: 'auto',
          opacity: webglReady ? 0 : 1,
          transition: `opacity ${tokens.durSlow} ${tokens.easeStandard}`,
          pointerEvents: 'none',
        }}
      >
        <polygon
          fill="none"
          stroke={tokens.gold}
          strokeWidth={13}
          strokeLinejoin="miter"
          points="36.610,10.740 81.610,88.682 -8.390,88.682"
        />
        <polygon
          fill={tokens.gold}
          points="51.621,-2.260 66.632,-2.260 122.891,95.182 107.880,95.182"
        />
      </svg>
    </div>
  )
}
